import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { MdSearch, MdShoppingCart, MdArrowBack, MdArrowForward } from 'react-icons/md';

const AnnouncementBar = styled.div`
  width: 100%;
  background: linear-gradient(90deg, rgba(11, 11, 11, 0.95) 0%, rgba(26, 26, 26, 0.98) 50%, rgba(11, 11, 11, 0.95) 100%);
  border-bottom: 1px solid rgba(212, 175, 55, 0.15);
  padding: 0.45rem 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  font-weight: 750;
  color: var(--color-champagne);
  text-transform: uppercase;
  letter-spacing: 0.18em;
  text-align: center;
  z-index: 10;
  position: relative;
  
  @media (max-width: 576px) {
    font-size: 0.6rem;
    padding: 0.35rem 0.5rem;
    letter-spacing: 0.12em;
  }
`;

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: transparent;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.2);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 15% 50%, rgba(212, 175, 55, 0.02) 0%, transparent 60%),
      radial-gradient(circle at 85% 50%, rgba(212, 175, 55, 0.015) 0%, transparent 60%);
    pointer-events: none;
    z-index: 1;
    animation: luxury-ambient-glow 25s ease-in-out infinite;
  }
`;

const HeaderContent = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 2;
  transition: all 0.4s ease;
  
  @media (max-width: 968px) {
    max-width: 100%;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.4s ease;
  
  &:hover {
    transform: scale(1.06);
    filter: brightness(1.15);
  }
`;

const LogoImage = styled.img`
  height: 100%;
  width: auto;
  object-fit: contain;
  transition: all 0.3s ease;
`;

const Navigation = styled.nav`
  display: flex;
  gap: 1.5rem;
  align-items: center;
  
  @media (max-width: 1024px) {
    gap: 1.15rem;
  }
  @media (max-width: 968px) {
    gap: 0.85rem;
  }
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled.a`
  color: #ffffff;
  font-weight: 550;
  font-size: 0.85rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  position: relative;
  cursor: pointer;
  padding: 0.4rem 0.6rem;
  transition: color 0.3s ease;
  
  &:hover {
    color: var(--color-champagne);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 1px;
    background: var(--color-champagne);
    transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  &:hover::after {
    width: 80%;
  }
`;

const CTAButton = styled.button`
  background: transparent;
  color: var(--color-champagne);
  padding: 0.5rem 1.25rem;
  border-radius: 20px;
  font-weight: 700;
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border: 1px solid var(--color-champagne);
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    background: var(--color-champagne);
    color: var(--color-obsidian);
    box-shadow: 0 0 15px rgba(212, 175, 55, 0.35);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  @media (max-width: 480px) {
    padding: 0.4rem 1rem;
    font-size: 0.7rem;
  }
`;

const HeroSection = styled.section`
  max-width: calc(100% - 1mm);
  width: calc(100% - 1mm);
  aspect-ratio: 2 / 1;
  margin: calc(120px + 0.5mm) auto 0.5mm;
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  border: none;
  background: var(--color-obsidian);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
  z-index: 10;
  
  @media (max-width: 1024px) {
    margin-top: calc(110px + 0.5mm);
    width: calc(100% - 1mm);
    max-width: calc(100% - 1mm);
  }
  
  @media (max-width: 768px) {
    margin-top: calc(95px + 0.5mm);
    width: calc(100% - 1mm);
    max-width: calc(100% - 1mm);
    border-radius: 4px;
  }
`;

const SlidesTrack = styled.div<{ currentSlide: number }>`
  display: flex;
  width: 100%;
  height: 100%;
  transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateX(-${props => props.currentSlide * 100}%);
`;

const HeroSlide = styled.div<{ backgroundImage: string }>`
  min-width: 100%;
  width: 100%;
  height: 100%;
  background-image: url(${props => props.backgroundImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;



const SearchBarContainer = styled.div`
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 20px;
  padding: 0.35rem 0.8rem;
  margin: 0 0.75rem;
  width: 220px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:focus-within {
    border-color: #ffffff;
    background: rgba(255, 255, 255, 0.05);
    box-shadow: 0 0 10px rgba(255, 255, 255, 0.2);
    width: 260px;
  }
  
  @media (max-width: 968px) {
    width: 170px;
    &:focus-within {
      width: 200px;
    }
  }
  @media (max-width: 768px) {
    display: none;
  }
`;

const SearchInput = styled.input`
  background: transparent;
  border: none;
  outline: none;
  color: #ffffff;
  font-size: 0.8rem;
  width: 100%;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.85);
  }
`;

const CartButton = styled.button`
  background: transparent;
  color: #ffffff;
  border: none;
  font-size: 1.35rem;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: pointer;
  padding: 0.45rem;
  border-radius: 50%;
  transition: all 0.3s ease;
  margin-right: 0.5rem;
  border: 1px solid transparent;
  
  &:hover {
    color: var(--color-champagne);
    background: rgba(212, 175, 55, 0.05);
    border-color: rgba(212, 175, 55, 0.15);
  }
`;

const CartBadge = styled.span`
  position: absolute;
  top: -3px;
  right: -3px;
  background: linear-gradient(135deg, var(--color-champagne) 0%, var(--color-rose-gold) 100%);
  color: var(--color-obsidian);
  font-size: 0.65rem;
  font-weight: 900;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
`;

const CarouselArrow = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(11, 11, 11, 0.6);
  color: var(--color-slate-300);
  border: 1px solid rgba(212, 175, 55, 0.2);
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 10;
  transition: all 0.4s ease;
  backdrop-filter: blur(8px);
  
  &:hover {
    color: var(--color-champagne);
    border-color: var(--color-champagne);
    background: rgba(11, 11, 11, 0.8);
    transform: translateY(-50%) scale(1.1);
  }
  
  &.prev {
    left: 2rem;
  }
  
  &.next {
    right: 2rem;
  }
  
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 1.2rem;
    
    &.prev { left: 0.5rem; }
    &.next { right: 0.5rem; }
  }
`;

const CarouselDots = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 0.8rem;
  margin-bottom: 0.75rem;
  z-index: 10;
`;

const CarouselDot = styled.button<{ isActive: boolean }>`
  width: ${props => props.isActive ? '24px' : '8px'};
  height: 8px;
  border-radius: 4px;
  background: ${props => props.isActive ? 'var(--color-champagne)' : 'rgba(255, 255, 255, 0.3)'};
  border: none;
  cursor: pointer;
  transition: all 0.4s ease;
  
  &:hover {
    background: var(--color-champagne);
  }
`;

const slides = [
  {
    image: '/assets/BANNERS/1.jpg',
    heading: 'MUSEUM QUALITY',
    highlight: 'COLLECTIBLES',
    tagline: 'Hand-sculpted, limited edition action figures and dioramas of iconic legends.',
    category: ''
  },
  {
    image: '/assets/BANNERS/2.jpg',
    heading: 'THE GOD OF MISCHIEF',
    highlight: 'LOKI STATUE',
    tagline: 'Quarter scale masterpiece, detailed armor paintwork and custom base.',
    category: 'Avengers'
  },
  {
    image: '/assets/BANNERS/3.jpg',
    heading: 'VENOM VS SPIDER-MAN',
    highlight: 'BATTLE DIORAMA',
    tagline: 'Clash of the comic titans. Dynamic symbiote tendrils and detailed expressions.',
    category: 'Spider-Man'
  },
  {
    image: '/assets/BANNERS/4.jpg',
    heading: 'GUARDIANS OF THE GALAXY',
    highlight: 'ROCKET & GROOT',
    tagline: 'Celebrate Marvel’s most beloved cosmic team, featuring detailed fur styling.',
    category: 'Guardians of the Galaxy'
  }
];

interface HeaderProps {
  cartCount: number;
  onCartClick: () => void;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  currentPage: 'home' | 'shop';
  setCurrentPage: (page: 'home' | 'shop') => void;
}

const Header: React.FC<HeaderProps> = ({ 
  cartCount, 
  onCartClick, 
  searchTerm, 
  onSearchChange,
  currentPage,
  setCurrentPage
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const handleNavigation = (page: 'home' | 'shop', sectionId?: string) => {
    setCurrentPage(page);
    if (sectionId) {
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 150);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const scrollToContact = () => {
    if (currentPage !== 'home') {
      setCurrentPage('home');
      setTimeout(() => {
        const footer = document.querySelector('footer');
        if (footer) footer.scrollIntoView({ behavior: 'smooth' });
      }, 150);
    } else {
      const footer = document.querySelector('footer');
      if (footer) footer.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleBannerClick = (category: string) => {
    onSearchChange(category);
    setCurrentPage('shop');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <HeaderContainer style={{
        background: isScrolled ? 'rgba(11, 11, 11, 0.96)' : 'rgba(11, 11, 11, 0.15)',
        borderBottom: isScrolled ? '1px solid rgba(212, 175, 55, 0.2)' : '1px solid rgba(212, 175, 55, 0.05)',
        backdropFilter: 'blur(30px) saturate(1.2)'
      }}>
        <AnnouncementBar>
          Free Shipping on all Collectibles • Limited Edition Masterpieces
        </AnnouncementBar>
        <HeaderContent style={{
          padding: isScrolled ? '0.65rem 2rem' : '1.15rem 2rem'
        }}>
          <LogoContainer 
            onClick={() => handleNavigation('home')}
            style={{ height: isScrolled ? '46px' : '58px' }}
          >
            <LogoImage src="/assets/logo/withouttag.png" alt="KIII Toys Studios Logo" />
          </LogoContainer>
          <Navigation>
            <NavLink onClick={() => handleNavigation('shop', 'gallery')}>Shop</NavLink>
            <NavLink onClick={() => handleNavigation('home', 'about')}>About</NavLink>
            <NavLink onClick={() => handleNavigation('home', 'process')}>Process</NavLink>
            <NavLink onClick={() => handleNavigation('home', 'subscribe')}>Subscribe</NavLink>
          </Navigation>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <SearchBarContainer>
              {React.createElement(MdSearch as React.ComponentType<any>, {
                style: { marginRight: '0.4rem', color: '#ffffff', fontSize: '1.2rem' }
              })}
              <SearchInput 
                type="text" 
                placeholder="Search Collectibles..." 
                value={searchTerm} 
                onChange={(e) => {
                  onSearchChange(e.target.value);
                  handleNavigation('shop', 'gallery');
                }} 
              />
            </SearchBarContainer>

            <CartButton onClick={onCartClick} aria-label="Open Shopping Cart">
              {React.createElement(MdShoppingCart as React.ComponentType<any>)}
              {cartCount > 0 && <CartBadge>{cartCount}</CartBadge>}
            </CartButton>

            <CTAButton onClick={scrollToContact}>
              Contact Us
            </CTAButton>
          </div>
        </HeaderContent>
      </HeaderContainer>

      {currentPage === 'home' && (
        <HeroSection>
          <SlidesTrack currentSlide={currentSlide}>
            {slides.map((slide, index) => (
              <HeroSlide 
                key={index} 
                backgroundImage={slide.image} 
                style={{ cursor: 'pointer' }}
                onClick={() => handleBannerClick(slide.category)}
              />
            ))}
          </SlidesTrack>

          <CarouselArrow className="prev" onClick={(e) => { e.stopPropagation(); prevSlide(); }} aria-label="Previous Slide">
            {React.createElement(MdArrowBack as React.ComponentType<any>)}
          </CarouselArrow>
          <CarouselArrow className="next" onClick={(e) => { e.stopPropagation(); nextSlide(); }} aria-label="Next Slide">
            {React.createElement(MdArrowForward as React.ComponentType<any>)}
          </CarouselArrow>

        </HeroSection>
      )}

      {currentPage === 'home' && (
        <CarouselDots>
          {slides.map((_, index) => (
            <CarouselDot 
              key={index} 
              isActive={currentSlide === index} 
              onClick={(e) => { e.stopPropagation(); setCurrentSlide(index); }} 
            />
          ))}
        </CarouselDots>
      )}
    </>
  );
};

export default Header;